import type { ActivityLines, ToolActivity } from "../protocol/protocol";
import type { ToolView } from "../store/selectors";

/**
 * Generic phrases for a call with no line of its own (null): the backend's GENERIC_ACTIVITY words
 * (apps/agentic/tools/base.py), which own this copy. A denied call reads as not done. Never the
 * tool's name.
 */
const FALLBACK = {
  running: "Working on it",
  success: "Done",
  failed: "Didn't finish that",
  denied: "Didn't finish that",
  retry: "Working on it",
} as const satisfies Record<ToolView["state"], string>;

/** Which of the tool's own lines fits a call's state; a declined call reads as not done. */
const LINE_FOR_STATE: Record<ToolView["state"], keyof ActivityLines> = {
  running: "running",
  retry: "running",
  success: "succeeded",
  failed: "failed",
  denied: "failed",
};

/**
 * The one line a tool call shows: the tool's line for its state (its awaiting-approval line while
 * a choice waits), else a generic phrase for the state. A single string is the call's running
 * title, so it shows only while the call runs: an outcome is never read from a title.
 */
export function toolLine(
  state: ToolView["state"],
  activity: ToolActivity,
  awaitingChoice = false
): string {
  if (typeof activity === "string" || activity == null) {
    const running = state === "running" || state === "retry";
    return (running && activity?.trim()) || FALLBACK[state];
  }
  const key = awaitingChoice && state === "running" ? "awaiting_approval" : LINE_FOR_STATE[state];
  return activity[key]?.trim() || FALLBACK[state];
}
